<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-medium"
    title="IPマッピング設定参照"
  >
    <template v-slot:body="{ cancel }">
      <v-card-text class="pa-0">
        <div class="modal-body">
          <v-container>
            <v-row>
              <v-col class="text-right">
                <v-btn
                  v-permission.config
                  color="primary"
                  @click="showModifyIpMappingModal"
                  >変更</v-btn
                >
                <v-btn
                  v-if="!ipMapping.isPolicyInUse"
                  v-permission.config
                  color="primary"
                  @click="deleteIpMapping"
                  class="ml-2"
                  >削除</v-btn
                >
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <app-label-group
                  label="名前"
                  :value="ipMapping.ipMappingName"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <app-label-group
                  label="グローバルIP"
                  :value="ipMapping.globalIpAddress"
                />
              </v-col>
            </v-row>
            <v-row v-if="'dstZone' in ipMapping && ipMapping.dstZone">
              <v-col cols="12">
                <app-label-group
                  label="宛先ゾーン"
                  :value="ipMapping.dstZone.zone.zoneName"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <app-label-group
                  label="プライベートIP"
                  :value="ipMapping.privateIpAddress"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <app-label-group
                  label="ポートフォワード"
                  :value="$filter.enumTo(ipMapping.isPortForward, 'enable')"
                />
              </v-col>
            </v-row>
            <template v-if="ipMapping.isPortForward">
              <v-row>
                <v-col cols="12">
                  <app-label-group
                    label="プロトコル"
                    :value="ipMapping.protocol"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <app-label-group
                    label="グローバル側Port"
                    :value="ipMapping.globalPort"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <app-label-group
                    label="プライベート側Port"
                    :value="ipMapping.privatePort"
                  />
                </v-col>
              </v-row>
            </template>
            <v-row>
              <v-col cols="12">
                <app-label-group
                  label="備考"
                  :value="$filter.emptyTo(ipMapping.description, '-')"
                  class="text-pre-wrap"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <app-label-group
                  label="ポリシー使用有無"
                  :value="
                    $filter.enumTo(ipMapping.isPolicyInUse, 'isPolicyInUse')
                  "
                />
              </v-col>
            </v-row>
          </v-container>
        </div>
      </v-card-text>
      <v-divider></v-divider>

      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn @click="cancel" variant="outlined" color="primary"
              >戻る</v-btn
            >
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import { VueFinalModal } from "vue-final-modal";
import type { DeleteIpMapping, IpMapping } from "@/apis/IpMappingApi";
import IpMappingType4Edit from "@/modals/applianceObject/IpMappingType4Edit.vue";
import type { Type1IpMappingItem } from "@/views/nwSetting/applianceObject/Type1IpMapping.vue";
import IpMappingType1Edit from "@/modals/applianceObject/IpMappingType1Edit.vue";

export default defineComponent({
  name: "IpMappingReference",
  props: {
    /** type1 IPマッピング or type4 IPマッピング */
    ipMapping: {
      type: Object as PropType<Type1IpMappingItem | IpMapping>,
      required: true,
    },
    /** 排他制御 */
    ipMappingKeyId: {
      type: String as PropType<string>,
      required: true,
    },
    /** Type4 ID。Type4のIPマッピングの場合は必須 */
    type4Id: {
      type: String as PropType<string>,
      required: false,
      default: null,
    },
  },
  methods: {
    /**
     * IP Poolの削除処理
     */
    async deleteIpMapping(): Promise<void> {
      // ポリシー設定有無が有りの場合は処理を終了
      if (this.ipMapping.isPolicyInUse) return;

      // 確認ダイアログの表示
      await this.$confirm("IPマッピングを削除します。よろしいですか？");

      // リクエスト用データの作成
      const reqData: DeleteIpMapping = {
        ipMappingList: [this.ipMapping.virtualIpAddressSeq],
        ipMappingKeyId: this.ipMappingKeyId,
        note: null,
      };

      // IPマッピング削除APIを呼び出す
      if (!this.type4Id) {
        await this.$api.ipMapping.deleteType1IpMapping(reqData);
      } else {
        await this.$api.ipMapping.deleteType4IpMapping(this.type4Id, reqData);
      }
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
    /**
     * 変更モーダルを表示する
     */
    async showModifyIpMappingModal(): Promise<void> {
      if (this.type4Id) {
        await this.$modal.show(IpMappingType4Edit, {
          ipMapping: this.ipMapping,
          ipMappingKeyId: this.ipMappingKeyId,
          type4Id: this.type4Id,
        });
      } else {
        await this.$modal.show(IpMappingType1Edit, {
          ipMapping: this.ipMapping,
          ipMappingKeyId: this.ipMappingKeyId,
        });
      }
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
  },
});
</script>
